import React from 'react'
import { createRoot } from 'react-dom/client'
import './theme.css'
import { App } from './App'
import { startOutbox } from './api'

/**
 * A render crash must never leave a blank page. It says what broke and offers
 * a way out, because an unsaved edit may still be sitting in the outbox.
 */
class Boundary extends React.Component<{ children: React.ReactNode }, { err: Error | null }> {
  state = { err: null as Error | null }
  static getDerivedStateFromError(err: Error) { return { err } }
  componentDidCatch(err: Error, info: unknown) { console.error('Vantage crashed:', err, info) }
  render() {
    if (!this.state.err) return this.props.children
    return (
      <div style={{ padding: 40, maxWidth: 680, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
        <h1 style={{ fontSize: 19, fontWeight: 600, marginBottom: 8 }}>Something broke on this screen</h1>
        <p style={{ fontSize: 13.5, color: '#6B7280', marginBottom: 16 }}>
          Your data is safe — nothing is saved from the screen, only from the server. Any edit you had
          queued is still in the outbox and will go up on reload.
        </p>
        <pre style={{ fontSize: 11.5, background: '#F2F3F5', color: '#B42318', padding: 12,
          borderRadius: 8, overflow: 'auto', marginBottom: 16 }}>{String(this.state.err?.message ?? this.state.err)}</pre>
        <button onClick={() => { location.hash = '#/'; location.reload() }}
          style={{ padding: '8px 14px', borderRadius: 7, border: '1px solid #15171C',
            background: '#15171C', color: '#fff', fontSize: 12.5, cursor: 'pointer' }}>
          Back to the project
        </button>
      </div>
    )
  }
}

const saved = localStorage.getItem('vantage-theme')
document.documentElement.setAttribute('data-theme',
  saved ?? (matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'))

startOutbox()
createRoot(document.getElementById('root')!).render(
  <React.StrictMode><Boundary><App /></Boundary></React.StrictMode>,
)
